import { RTCInboundRtpVideoStreamStats } from './types';
import { Queue } from 'utility/queue';
import { StreamSelectorBase } from './StreamSelectorBase';

function sum(arr: number[]) {
    return arr.reduce((prev, current) => prev + current, 0);
}

function avg(arr: number[]) {
    return Math.floor(sum(arr) / arr.length);
}

function getLatestFramerate(stats: RTCInboundRtpVideoStreamStats[]) {
    const last = stats.at(-1);
    if (!last) return undefined;

    //statsが更新されていない場合-1を返す
    const currentTimestamp = new Date().getTime();
    if (currentTimestamp - last.timestamp > 50) return undefined;

    // calculate fps if possible
    if (stats.length > 1) {
        const first = stats.at(-10) ?? stats[0];
        if (last.framesDecoded !== undefined && first.framesDecoded !== undefined) {
            const calculated = (last.framesDecoded - first.framesDecoded) * 1000 / (last.timestamp - first.timestamp);
            return Math.min(calculated, last.framesPerSecond);
        }
    }

    return last.framesPerSecond;
}

function cap(min: number, max: number) {
    return (value: number) => value >= min && value <= max;
}

export class StreamSelectorByFramerate extends StreamSelectorBase {
    private framerateTable = new Array<Queue<number>>();

    label() { return 'framerate'; }

    description() {
        return this.context;
    }

    batchUpdate(streams: Queue<RTCInboundRtpVideoStreamStats>[]): void {
        streams.forEach((stats, index) => {
            const framerate = getLatestFramerate(stats.array);
            if (framerate === undefined) return;

            const frameRates = this.framerateTable[index] = this.framerateTable.at(index) ?? new Queue(10);
            frameRates.push(framerate);
        });
    }

    private context: Record<string, unknown> = {};

    get(): number | 'matched' | 'deferred' {
        const values = this.framerateTable;
        const threshHigh = 1.4;

        this.context = { stats: values.map(q => [...q.array]) };

        const averageFrameRates = values.map((q, index) => ({ avg: q.length > 0 ? avg(q.array.filter(cap(0, 100))) : -1, index }));
        this.context = { ...this.context, averageFrameRates };

        const [top, next] = averageFrameRates.sort((a, b) => b.avg - a.avg);
        this.context = { ...this.context, top, next };
        if (top.avg < 0) return 'deferred';

        if (top.avg > next.avg * threshHigh) return top.index;

        return 'matched';
    }
}
